import { Button, Col, Form, Input, message, Row, Select } from 'antd';
import { useTheme } from '../../../providers/theme';
import { dropdownStyle, getColors } from '../../utils/getColors';
import { AddProjectPayload } from '../../../@types/projects/types';
import {
  positiveNumberValidator,
  requiredValidator,
  zeroToOneValidator
} from '../../utils/formRules';
import { AnalyzeType, RockType } from '../../../@types/samples/types';
import { useState } from 'react';
import { useAppContext } from '../../../providers/AppContext';
import { validateAddProject } from '../utils';
import { addProject } from '../../../api/data';

type ProjectFormProps = {
  handleCancel: () => void;
};

const ProjectForm = ({ handleCancel }: ProjectFormProps) => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const [loading, setLoading] = useState(false);
  const { updateConfigStates, updateData } =
    useAppContext();
  const [form] = Form.useForm<AddProjectPayload>();

  const handleSubmit = async (statePayload: AddProjectPayload) => {
    const payload: AddProjectPayload = {
      project_name: statePayload.project_name,
      country: statePayload.country,
      block: statePayload.block,
      reservoir: statePayload.reservoir,
      rock_type: statePayload.rock_type,
      type_of_analyze: AnalyzeType.LIAO
    };

    const errors = validateAddProject(payload);

    if (errors.length) {
      const mappedErrors = errors.map((error, index) => (
        <div key={`error-${index}`}>- {error}</div>
      ));
      message.error(
        <div>
          The following error(s) ocurred:
          <div>{mappedErrors}</div>
        </div>
      );

      return;
    }

    setLoading(true);

    try {
      const newProject = await addProject(payload);
      updateData(previous => ({
        projects: [newProject, ...previous.projects],
      }));
      updateConfigStates({ showProject: false });
      message.success('Project created successfully');
    } catch (error) {
      message.error('Error creating project');
      console.error('Error creating project:', error);
    } finally {
      setLoading(false);
      form.resetFields();
    }
  };

  return (
    <div style={{ marginTop: '1rem' }}>
      <Form
        form={form}
        onFinish={handleSubmit}
        initialValues={{
          rock_type: RockType.SANDSTONE
        }}
      >
        <div>
          <h6>Name</h6>
          <Form.Item name="project_name">
            <Input />
          </Form.Item>
          <Row gutter={[24, 16]}>
            <Col span={12}>
              <h6>Country</h6>
              <Form.Item name="country">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <h6>Block/Field</h6>
              <Form.Item name="block">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[24, 16]}>
            <Col span={12}>
              <h6>Reservoir</h6>
              <Form.Item name="reservoir">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <h6>Rock Type</h6>
              <Form.Item name="rock_type">
                <Select
                  style={{ width: '100%' }}
                  options={[
                    {
                      value: RockType.SANDSTONE,
                      label: 'Sandstone'
                    },
                    {
                      value: RockType.LIMESTONE,
                      label: 'Limestone'
                    }
                  ]}
                  dropdownStyle={dropdownStyle(isDay)}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>
        <div
          style={{ display: 'flex', justifyContent: 'end', marginTop: '2rem' }}
        >
          <div>
            <Button
              style={{
                color: colors.text,
                marginRight: '0.5rem'
              }}
              key="back"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
          <div>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
            >
              Create Project
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default ProjectForm;
